import { lumi } from '@/lib/lumi'
import { useCallback, useEffect, useState } from 'react'

export type ContentType = 'text' | 'richtext' | 'image' | 'url'

export interface SiteContentItem {
  _id: string
  key: string
  value: string
  type: ContentType
  section?: string
  label?: string
  creator?: string
  createdAt?: string
  updatedAt: string
}

export type ContentMap = Record<string, SiteContentItem>

/**
 * Hook central de gestion du contenu editable du site.
 * Charge tout le contenu en une map cle -> item et expose un upsert (saveContent).
 */
export function useSiteContent() {
  const [contentMap, setContentMap] = useState<ContentMap>({})
  const [loading, setLoading] = useState(true)

  const fetchContent = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.site_content.list({
        sort: { section: 1, key: 1 },
      })
      const map: ContentMap = {}
      ;(list as SiteContentItem[]).forEach((item) => {
        if (item?.key) map[item.key] = item
      })
      setContentMap(map)
    } catch (error) {
      console.error('Echec du chargement du contenu du site:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchContent()
  }, [fetchContent])

  /**
   * Upsert d'un bloc de contenu par cle.
   * - Si la cle existe deja -> update
   * - Sinon -> create
   */
  const saveContent = useCallback(
    async (
      key: string,
      value: string,
      meta?: { type?: ContentType; section?: string; label?: string }
    ) => {
      const now = new Date().toISOString()
      const existing = contentMap[key]

      if (existing?._id) {
        const updated = (await lumi.entities.site_content.update(existing._id, {
          value,
          updatedAt: now,
        })) as SiteContentItem
        setContentMap((prev) => ({
          ...prev,
          [key]: { ...existing, ...updated, value, updatedAt: now },
        }))
        return updated
      }

      const created = (await lumi.entities.site_content.create({
        key,
        value,
        type: meta?.type || 'text',
        section: meta?.section || '',
        label: meta?.label || key,
        createdAt: now,
        updatedAt: now,
      })) as SiteContentItem
      setContentMap((prev) => ({ ...prev, [key]: created }))
      return created
    },
    [contentMap]
  )

  const getValue = useCallback(
    (key: string, fallback = '') => contentMap[key]?.value ?? fallback,
    [contentMap]
  )

  return {
    contentMap,
    loading,
    fetchContent,
    saveContent,
    getValue,
  }
}
